import { useEffect, useMemo, useState } from 'react';
import { calcLunchBreaksMs } from '@/lib/utils';
import { getActiveDaysIfUnmanaged } from '@/lib/api/techActivity';
import { fetchStressTests } from '@/lib/api/stressTests';
import { useOrderPauses } from '@/lib/api/pauses';
import { countedPauses, orderPauseIntervals, type PauseRow } from '@/lib/pauses';
import {
  buildStageTimeline, resolveTimelineOrderEnd, assessBuildDuration, earliestStressStart,
  type StageRow, type BuildDuration,
} from '@/lib/stageTimeline';

export interface ReviewTimeline {
  stages: StageRow[];
  lunchMs: number;
  duration: BuildDuration | null;
  pauses: PauseRow[];
  activeDays: Set<string> | undefined;
  /** Pauses still running or awaiting a decision; posting is blocked while > 0. */
  unreviewedPauses: number;
}

/**
 * The build timeline a reviewer sees before posting: the stage rows, the
 * judged duration, and the order's pauses. Shared by the Production and
 * Assembly Orders review dialogs so both measure the same way as the order's
 * detail page.
 *
 * Recomputes when a pause is approved or rejected, so the assembly duration
 * shown is always the one that will be recorded.
 */
export function useReviewTimeline(order: any): ReviewTimeline {
  const [ctx, setCtx] = useState<{ id: string; activeDays?: Set<string>; stressStart: string | null } | null>(null);
  const { data: pauses = [] } = useOrderPauses(order?.id);

  useEffect(() => {
    if (!order?.id) { setCtx(null); return; }
    let cancelled = false;
    (async () => {
      const ts = order.stage_timestamps || {};
      // The stress log can predate every recorded tick; see assessBuildDuration.
      const stressStart = earliestStressStart(await fetchStressTests(order.id).catch(() => []));

      let activeDays: Set<string> | undefined;
      if (order.technician_id) {
        const allVals = Object.entries(ts)
          .filter(([k, v]) => k !== 'units' && typeof v === 'string')
          .map(([, v]) => v as string);
        const earliest = [order.started_at, stressStart, ...allVals].filter(Boolean).sort()[0] as string | undefined;
        if (earliest) {
          activeDays = await getActiveDaysIfUnmanaged(order.technician_id, new Date(earliest), new Date());
        }
      }
      if (!cancelled) setCtx({ id: order.id, activeDays, stressStart });
    })();
    return () => { cancelled = true; };
    // Keyed on the order, not the object: the dialog merges signatures into it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  return useMemo(() => {
    const unreviewedPauses = pauses.filter(p => p.review_status === 'pending' || !p.resumed_at).length;
    if (!order?.id || !ctx || ctx.id !== order.id) {
      return { stages: [], lunchMs: 0, duration: null, pauses, activeDays: undefined, unreviewedPauses };
    }
    const ts = order.stage_timestamps || {};
    const intervals = orderPauseIntervals(countedPauses(pauses), order.quantity || 1);
    const stages = buildStageTimeline(ts, {
      orderStart: order.started_at || null,
      orderEnd: resolveTimelineOrderEnd(order, ts),
      activeDays: ctx.activeDays,
      pauses: intervals,
    });
    const first = stages[0]?.started;
    const last = [...stages].reverse().find(r => r.completed)?.completed;
    return {
      stages,
      lunchMs: first && last ? calcLunchBreaksMs(new Date(first), new Date(last), ctx.activeDays) : 0,
      duration: assessBuildDuration(first, last, ctx.stressStart, ctx.activeDays, intervals),
      pauses,
      activeDays: ctx.activeDays,
      unreviewedPauses,
    };
  }, [order, ctx, pauses]);
}
